import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Animated, LayoutChangeEvent, Platform, View } from 'react-native';

import { Pressable, Text, createMqStyles, tokens } from '@fhs/ui';
import { focusStoreLocatorFirstRestaurantResult } from '@fhs-legacy/frontend/src/pages/store-locator/store-locator-focus-handoff';
import { OrderLocatorTab } from '@fhs-legacy/frontend/src/utils/launchdarkly/variations';

const isWeb = Platform.OS === 'web';

export const TAB_LABEL_IDS: Partial<Record<OrderLocatorTab, string>> = {
  [OrderLocatorTab.NEARBY]: 'nearby',
  [OrderLocatorTab.RECENT]: 'previous',
  [OrderLocatorTab.FAVORITE]: 'favorite',
};

/** Each tab controls its own pager scene, so `aria-controls` needs one id per tab. */
export const getLocatorPanelId = (panelId: string, tab: OrderLocatorTab) => `${panelId}-${tab}`;

interface LocatorTabsProps {
  tabs: OrderLocatorTab[];
  activeTab: OrderLocatorTab;
  onChange: (tab: OrderLocatorTab) => void;
  /** Base id of the panels the tabs control, so web can wire `aria-controls`. */
  panelId: string;
  /**
   * Pager offset in tab indices. Drives the underline so it tracks a swipe rather than
   * jumping once the pager settles. Omitted when the tabs are rendered without a pager.
   */
  position?: Animated.AnimatedInterpolation<number>;
}

export const LocatorTabs: React.VFC<LocatorTabsProps> = ({
  tabs,
  activeTab,
  onChange,
  panelId,
  position,
}) => {
  const { formatMessage } = useIntl();
  const styles = useLocatorTabsMqStyles();
  const tabListRef = useRef<View>(null);
  const [trackWidth, setTrackWidth] = useState(0);
  // Signing out drops Previous and Favorites, which can leave one of them as the active tab
  // for a render. Fall back to the first tab so the underline still has somewhere to sit.
  const activeIndex = Math.max(0, tabs.indexOf(activeTab));

  const labels = useMemo(
    () =>
      tabs.map(tab => ({
        tab,
        label: formatMessage({ id: TAB_LABEL_IDS[tab] ?? 'nearby' }),
      })),
    [tabs, formatMessage]
  );

  // Roving arrow-key navigation across the tabs, plus the existing handoff that sends Tab from
  // the selected tab straight to the first store card instead of every control in between.
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const target = event.target;
      const tabElement = target instanceof HTMLElement ? target.closest('[role="tab"]') : null;

      if (!(tabElement instanceof HTMLElement)) {
        return;
      }

      if (
        event.key === 'Tab' &&
        !event.shiftKey &&
        tabElement.getAttribute('aria-selected') === 'true' &&
        focusStoreLocatorFirstRestaurantResult()
      ) {
        event.preventDefault();
        return;
      }

      const currentIndex = tabs.indexOf(activeTab);
      let nextIndex: number | undefined;

      switch (event.key) {
        case 'ArrowRight':
          nextIndex = (currentIndex + 1) % tabs.length;
          break;
        case 'ArrowLeft':
          nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          nextIndex = 0;
          break;
        case 'End':
          nextIndex = tabs.length - 1;
          break;
        default:
          return;
      }

      event.preventDefault();
      onChange(tabs[nextIndex]);

      const nextTab = tabListRef.current as unknown as HTMLElement | null;
      const nextElement = nextTab?.querySelectorAll('[role="tab"]')[nextIndex];
      if (nextElement instanceof HTMLElement) {
        nextElement.focus();
      }
    },
    [activeTab, onChange, tabs]
  );

  useEffect(() => {
    if (!isWeb || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [onKeyDown]);

  const onTrackLayout = useCallback(
    (event: LayoutChangeEvent) => setTrackWidth(event.nativeEvent.layout.width),
    []
  );

  return (
    <View style={styles.container}>
      <View ref={tabListRef} style={styles.tabList} accessibilityRole="tablist">
        {labels.map(({ tab, label }) => (
          <View key={tab} style={styles.tab}>
            <LocatorTabButton
              tab={tab}
              label={label}
              isActive={tab === activeTab}
              panelId={panelId}
              onChange={onChange}
            />
          </View>
        ))}
      </View>

      {/*
       * The hairline and the red rule are siblings of the pressables rather than a
       * `borderBottomWidth` on them: `Pressable` applies its `borderWidth` prop as an inline
       * style, which on web outranks the class this stylesheet compiles to and would erase the
       * border. One indicator slides across a continuous track so it can follow a drag.
       */}
      <View style={styles.underlineTrack} onLayout={onTrackLayout}>
        <TabIndicator
          tabCount={tabs.length}
          activeIndex={activeIndex}
          trackWidth={trackWidth}
          position={position}
        />
      </View>
    </View>
  );
};

interface TabIndicatorProps {
  tabCount: number;
  activeIndex: number;
  trackWidth: number;
  position?: Animated.AnimatedInterpolation<number>;
}

const TabIndicator: React.VFC<TabIndicatorProps> = ({
  tabCount,
  activeIndex,
  trackWidth,
  position,
}) => {
  const styles = useLocatorTabsMqStyles();
  // Without a pager there is nothing animating the offset, so stand in for it with the
  // settled index. Same value shape either way, so the interpolation below has one code path.
  const settledPosition = useRef(new Animated.Value(activeIndex)).current;

  useEffect(() => {
    settledPosition.setValue(activeIndex);
  }, [activeIndex, settledPosition]);

  const offset = position ?? settledPosition;
  const tabWidth = trackWidth / tabCount;
  // `interpolate` needs at least two points to map between, so a lone tab (guests only ever
  // see Nearby) just sits at the start of the track.
  const isSlidable = trackWidth > 0 && tabCount > 1;

  const translateX = isSlidable
    ? offset.interpolate({
        inputRange: Array.from({ length: tabCount }, (_, index) => index),
        outputRange: Array.from({ length: tabCount }, (_, index) => index * tabWidth),
        extrapolate: 'clamp',
      })
    : 0;

  return (
    <Animated.View
      style={[
        styles.tabUnderlineActive,
        { width: trackWidth > 0 ? tabWidth : `${100 / tabCount}%` },
        isSlidable ? { transform: [{ translateX }] } : null,
      ]}
    />
  );
};

interface LocatorTabButtonProps {
  tab: OrderLocatorTab;
  label: string;
  isActive: boolean;
  panelId: string;
  onChange: (tab: OrderLocatorTab) => void;
}

const LocatorTabButton: React.VFC<LocatorTabButtonProps> = ({
  tab,
  label,
  isActive,
  panelId,
  onChange,
}) => {
  const styles = useLocatorTabsMqStyles();

  return (
    <Pressable
      testID={`store-locator-tab-${tab}`}
      accessibilityRole="tab"
      accessibilityState={{ selected: isActive }}
      dd-action-name={`store locator ${label} tab`}
      onPress={() => onChange(tab)}
      borderRadius={0}
      borderWidth={0}
      style={styles.tabButton}
      outlineStyle={styles.hideOutline}
      outlinePressedStyle={styles.hideOutline}
      outlineFocusedStyle={styles.hideOutline}
      outlineHoveredStyle={styles.hideOutline}
      outlineDisabledStyle={styles.hideOutline}
      {...(isWeb
        ? ({
            'aria-selected': isActive,
            'aria-controls': getLocatorPanelId(panelId, tab),
            tabIndex: isActive ? 0 : -1,
          } as object)
        : {})}
    >
      <Text.Ui
        size="md"
        weight="semibold"
        style={[styles.tabLabel, isActive && styles.tabLabelActive]}
      >
        {label}
      </Text.Ui>
    </Pressable>
  );
};

const useLocatorTabsMqStyles = createMqStyles({
  container: {
    $base: {
      paddingTop: 16,
    },
  },
  tabList: {
    $base: {
      flexDirection: 'row',
    },
  },
  tab: {
    $base: {
      flex: 1,
      overflow: 'hidden',
    },
  },
  tabButton: {
    $base: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingBottom: 12,
      paddingHorizontal: 16,
      width: '100%',
    },
  },
  hideOutline: {
    $base: {
      borderColor: tokens.colors.$transparent,
    },
  },
  underlineTrack: {
    $base: {
      backgroundColor: tokens.colors.$black10,
      height: 2,
    },
  },
  tabUnderlineActive: {
    $base: {
      backgroundColor: tokens.colors.$houseRed,
      height: 2,
    },
  },
  tabLabel: {
    $base: {
      color: tokens.colors.$houseDark,
    },
  },
  tabLabelActive: {
    $base: {
      color: tokens.colors.$houseRed,
    },
  },
});
